import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Phone, Mail, MapPin, Building, User, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useContent } from '../contexts/ContentContext';
import { contactAPI } from '../services/api';
import './ContactPage.css';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  message: string;
  agreedToTerms: boolean;
}

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { contactContent, getText, isLoading } = useContent();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    message: '',
    agreedToTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Create fallback default contactContent if not available
  const defaultContactContent = {
    pageTitle: { id: 'Hubungi Kami', en: 'Contact Us' },
    pageSubtitle: { id: 'Siap berinvestasi di Sukoharjo? Hubungi tim promosi investasi kami untuk membahas peluang Anda.', en: 'Ready to invest in Sukoharjo? Get in touch with our investment team to discuss your opportunities.' },
    infoTitle: { id: 'Hubungi Kami', en: 'Get In Touch' },
    infoDescription: { id: 'Tim promosi investasi kami siap membantu Anda dengan rencana investasi di Sukoharjo. Hubungi kami melalui saluran berikut:', en: 'Our investment promotion team is ready to assist you with your investment plans in Sukoharjo. Contact us through any of the following channels:' },
    formTitle: { id: 'Formulir Pertanyaan Investasi', en: 'Investment Inquiry Form' },
    formDescription: { id: 'Isi formulir ini dan tim kami akan menghubungi Anda dalam 24 jam.', en: 'Fill out this form and our team will get back to you within 24 hours.' },
    formLabels: {
      fullName: { id: 'Nama Lengkap', en: 'Full Name' },
      email: { id: 'Alamat Email', en: 'Email Address' },
      phone: { id: 'Nomor Telepon', en: 'Phone Number' },
      company: { id: 'Nama Perusahaan', en: 'Company Name' },
      position: { id: 'Posisi/Jabatan', en: 'Position/Title' },
      message: { id: 'Pesan', en: 'Message' },
      consent: { id: 'Saya setuju dengan pemrosesan data pribadi saya untuk tujuan promosi investasi dan menyetujui untuk dihubungi oleh DPMPTSP Sukoharjo terkait pertanyaan investasi saya.', en: 'I agree to the processing of my personal data for investment promotion purposes and consent to be contacted by DPMPTSP Sukoharjo regarding my investment inquiry.' },
      submit: { id: 'Kirim Pertanyaan', en: 'Send Inquiry' },
      submitting: { id: 'Mengirim...', en: 'Submitting...' }
    },
    successMessage: {
      title: { id: 'Terima Kasih!', en: 'Thank You!' },
      message: { id: 'Pertanyaan Anda telah berhasil dikirim. Tim kami akan menghubungi Anda dalam 24 jam.', en: 'Your inquiry has been submitted successfully. Our team will contact you within 24 hours.' }
    },
    errorMessage: {
      title: { id: 'Error', en: 'Error' },
      message: { id: 'Terjadi kesalahan saat mengirim formulir. Silakan coba lagi nanti.', en: 'There was an error submitting your form. Please try again later.' }
    },
    contactInfo: {
      id: 1,
      office: { id: 'DPMPTSP Sukoharjo', en: 'DPMPTSP Sukoharjo' },
      description: { id: 'Dinas Penanaman Modal dan Pelayanan Terpadu Satu Pintu Kabupaten Sukoharjo', en: 'Investment and Integrated Services Office of Sukoharjo Regency' },
      address: 'Jl. Jend. Sudirman No. 1, Sukoharjo, Central Java 57512, Indonesia',
      phone: '+62 271 593156',
      email: 'dpmptsp@sukoharjokab.go.id',
      officeHours: { id: 'Senin - Jumat: 08:00 - 16:00 WIB\nSabtu: 08:00 - 12:00 WIB\nMinggu: Tutup', en: 'Monday - Friday: 08:00 - 16:00 WIB\nSaturday: 08:00 - 12:00 WIB\nSunday: Closed' }
    }
  };

  // Use contactContent from context or fallback to default
  const currentContactContent = contactContent || defaultContactContent;
  
  // Show loading state only if explicitly loading
  if (isLoading) {
    return (
      <div className="contact-page">
        <Navbar />
        <div className="contact-header">
          <div className="container">
            <div className="header-content">
              <h1 className="page-title">Loading...</h1>
              <p className="page-subtitle">Please wait while we load the page content.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkboxInput = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkboxInput.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreedToTerms) {
      alert('Please agree to the terms and conditions');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Submit form data to backend API
      const response = await contactAPI.submit({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        position: formData.position,
        message: formData.message
      });
      
      if (response.success) {
        setSubmitStatus('success');
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            company: '',
            position: '',
            message: '',
            agreedToTerms: false
          });
          setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to submit form');
      }
      
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="contact-page">
      {/* Navbar */}
      <Navbar />
      
      {/* Header */}
      <div className="contact-header">
        <div className="container">
          <div className="header-content">
            <h1 className="page-title">{getText(currentContactContent.pageTitle)}</h1>
            <p className="page-subtitle">
              {getText(currentContactContent.pageSubtitle)}
            </p>
          </div>
        </div>
      </div>

      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2 className="info-title">{getText(currentContactContent.infoTitle)}</h2>
              <p className="info-description">
                {getText(currentContactContent.infoDescription)}
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Building className="w-6 h-6" />
                  </div>
                  <div className="contact-text">
                    <h4>{getText(currentContactContent.contactInfo.office)}</h4>
                    <p>{getText(currentContactContent.contactInfo.description)}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="contact-text">
                    <h4>Address</h4>
                    <p>{currentContactContent.contactInfo.address}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div className="contact-text">
                    <h4>Phone</h4>
                    <p>{currentContactContent.contactInfo.phone}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="contact-text">
                    <h4>Email</h4>
                    <p>{currentContactContent.contactInfo.email}</p>
                  </div>
                </div>
              </div>

              <div className="office-hours">
                <h4>Office Hours</h4>
                <p style={{ whiteSpace: 'pre-line' }}>{getText(currentContactContent.contactInfo.officeHours)}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form">
              <h2 className="form-title">{getText(currentContactContent.formTitle)}</h2>
              <p className="form-description">
                {getText(currentContactContent.formDescription)}
              </p>

              {submitStatus === 'success' && (
                <div className="alert alert-success">
                  <h4>{getText(currentContactContent.successMessage.title)}</h4>
                  <p>{getText(currentContactContent.successMessage.message)}</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="alert alert-error">
                  <h4>{getText(currentContactContent.errorMessage.title)}</h4>
                  <p>{getText(currentContactContent.errorMessage.message)}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="inquiry-form">
                <div className="form-group">
                  <label htmlFor="fullName">
                    <User className="w-4 h-4" />
                    {getText(currentContactContent.formLabels.fullName)} *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder={getText(currentContactContent.formLabels.fullName)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      <Mail className="w-4 h-4" />
                      {getText(currentContactContent.formLabels.email)} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <Phone className="w-4 h-4" />
                      {getText(currentContactContent.formLabels.phone)} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+62 xxx xxx xxx"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">
                      <Building className="w-4 h-4" />
                      {getText(currentContactContent.formLabels.company)}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder={getText(currentContactContent.formLabels.company)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="position">
                      <Building className="w-4 h-4" />
                      {getText(currentContactContent.formLabels.position)}
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder={getText(currentContactContent.formLabels.position)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <MessageCircle className="w-4 h-4" />
                    {getText(currentContactContent.formLabels.message)} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    placeholder={getText(currentContactContent.formLabels.message)}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreedToTerms"
                      checked={formData.agreedToTerms}
                      onChange={handleInputChange}
                      required
                    />
                    <span className="checkmark"></span>
                    {getText(currentContactContent.formLabels.consent)}
                  </label>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      {getText(currentContactContent.formLabels.submitting)}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {getText(currentContactContent.formLabels.submit)}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
